import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import {
  aboveBottomStrip,
  RAIL_WIDTH_VAR,
} from "@/components/common/bottom-nav/nav-constants";
import { getPinnedProject, getQueryCache, type PinnedProject } from "@/offline/db";
import { useOnlineStatus } from "@/offline/online-status";
import { isPinnedSync } from "@/offline/pins";
import { getPersisterDebugState } from "@/offline/debug-state";

interface OfflineDebugOverlayProps {
  projectId?: string;
}

/**
 * Diagnostic overlay that surfaces the offline subsystem's state on-screen.
 * Gated by `?debug=1` so it never leaks into the normal UX. Mounted from
 * `ProjectLayout` so the most common offline failure (records grid empty on
 * a pinned project) can be triaged from a phone without devtools.
 *
 * What it shows:
 *  - Connectivity (navigator vs the `useOnlineStatus` hook).
 *  - The pinned-project IDB row + sync status.
 *  - Every records / plans / projects query currently in the runtime
 *    QueryCache and whether it has data.
 *  - Persister-restore timing (boot → restoredAt) and any error phase
 *    captured by `createIDBPersister`'s `onError`.
 *  - Raw IDB `queryClientCache` row existence — proves whether the on-disk
 *    snapshot is empty, present-but-orphaned, or correctly hydrated.
 */
export function OfflineDebugOverlay({ projectId }: OfflineDebugOverlayProps) {
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const [pinRow, setPinRow] = useState<PinnedProject | null | undefined>(undefined);
  const [rawCacheBytes, setRawCacheBytes] = useState<number | null | undefined>(undefined);
  const [, forceTick] = useState(0);

  // Re-read IDB whenever the projectId or online status changes; also tick
  // every 2s while the overlay is mounted so cache mutations are visible
  // without a manual refresh.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void (async () => {
      const [row, raw] = await Promise.all([
        getPinnedProject(projectId).catch((err) => {
          // The overlay degrades to "(missing)" on a read failure, but a
          // failed IDB read is still worth surfacing — see [[error-reporting]]
          // "never log-and-swallow".
          Sentry.captureException(err, {
            level: "warning",
            tags: { area: "offline-debug", phase: "idb-read" },
          });
          return null;
        }),
        getQueryCache().catch((err) => {
          Sentry.captureException(err, {
            level: "warning",
            tags: { area: "offline-debug", phase: "idb-read" },
          });
          return null;
        }),
      ]);
      if (cancelled) return;
      setPinRow(row ?? null);
      // Estimate raw-cache size by JSON-stringifying. Cheap, approximate.
      // null → no IDB row at all; otherwise byte length.
      if (raw == null) setRawCacheBytes(null);
      else {
        try {
          setRawCacheBytes(JSON.stringify(raw).length);
        } catch (err) {
          // `JSON.stringify` throws on a circular structure — the overlay
          // shows "(unserializable)", but report it too rather than swallow.
          Sentry.captureException(err, {
            level: "warning",
            tags: { area: "offline-debug", phase: "cache-serialize" },
          });
          setRawCacheBytes(-1);
        }
      }
    })();
    const id = setInterval(() => forceTick((n) => n + 1), 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [projectId, isOnline]);

  if (typeof window === "undefined") return null;
  if (!new URLSearchParams(window.location.search).has("debug")) return null;

  const debug = getPersisterDebugState();
  const restoreMs =
    debug.restoredAt !== null ? debug.restoredAt - debug.bootAt : null;

  // Inspect every cached query so the overlay shows what's actually
  // hydrated, not just what the records hook returns at this instant.
  const queries = queryClient.getQueryCache().getAll();
  const interesting = queries.filter((q) =>
    ["records", "plans", "projects"].includes(String(q.queryKey[0])),
  );

  function describeQuery(queryKey: ReadonlyArray<unknown>, data: unknown) {
    if (!data) return "no-data";
    if (
      typeof data === "object" &&
      data !== null &&
      "pages" in (data as Record<string, unknown>)
    ) {
      const pages = (data as { pages?: Array<{ results?: unknown[] }> }).pages ?? [];
      const total = pages.reduce(
        (sum, p) => sum + (Array.isArray(p?.results) ? p.results.length : 0),
        0,
      );
      void queryKey;
      return `pages=${pages.length} rows=${total}`;
    }
    return "scalar";
  }

  return (
    <div
      data-testid="offline-debug-overlay"
      style={{
        position: "fixed",
        bottom: aboveBottomStrip(0),
        left: `var(${RAIL_WIDTH_VAR}, 0px)`,
        right: 0,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.92)",
        color: "#e5e7eb",
        padding: "8px 12px",
        fontSize: 11,
        lineHeight: 1.35,
        fontFamily: "ui-monospace, Menlo, monospace",
        maxHeight: "45vh",
        overflowY: "auto",
        borderTop: "1px solid #374151",
      }}
    >
      <div style={{ color: "#10b981", marginBottom: 4 }}>
        offline-debug · tap "?debug=" off the URL to hide
      </div>

      <div>navigator.onLine={String(navigator.onLine)} · hook.online={String(isOnline)}</div>
      <div>projectId={projectId ?? "(none)"}</div>
      <div>
        isPinnedSync={projectId ? String(isPinnedSync(projectId)) : "—"} · pinRow=
        {pinRow === undefined
          ? "(loading)"
          : pinRow === null
            ? "(missing)"
            : `${pinRow.status} rec=${pinRow.progress.recordsDownloaded}/${pinRow.progress.recordsTotal} plan=${pinRow.progress.plansDownloaded}/${pinRow.progress.plansTotal}`}
      </div>

      <div>
        persister: restoredAt={debug.restoredAt === null ? "—" : `+${restoreMs}ms`}
        {debug.restoreErrorPhase
          ? ` · ERROR(${debug.restoreErrorPhase}): ${debug.restoreErrorMessage}`
          : ""}
      </div>
      <div>
        raw IDB queryClientCache row:{" "}
        {rawCacheBytes === undefined
          ? "(loading)"
          : rawCacheBytes === null
            ? "(missing)"
            : rawCacheBytes === -1
              ? "(unserializable)"
              : `~${rawCacheBytes}B`}
      </div>

      <div style={{ marginTop: 4 }}>
        queryCache: {interesting.length} matching
      </div>
      {interesting.map((q, i) => (
        <div key={i} style={{ paddingLeft: 8 }}>
          [{i}] {q.state.status} key={JSON.stringify(q.queryKey)}{" "}
          {describeQuery(q.queryKey, q.state.data)}{" "}
          {q.state.error ? `err=${(q.state.error as Error).name}` : ""}
        </div>
      ))}
    </div>
  );
}
